import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Status } from '@/types';
import { cn } from '@/lib/utils';
import useOpportunityStatusLabels from '@/hooks/runner/useOpportunityStatusLabels';
// Status styles mapping with text and background colors
const statusStyles: Record<
	Status,
	{ textColor: string; backgroundColor: string }
> = {
	Unsaved: { textColor: '#e95c63', backgroundColor: '#FBE8E9' },
	Draft: { textColor: '#6F6F6F', backgroundColor: '#F0F0F0' },
	Interested: { textColor: '#F9A825', backgroundColor: '#FFF8E1' },
	'In progress': { textColor: '#FF832B', backgroundColor: '#FFF2E9' },
	'In communication': { textColor: '#FF832B', backgroundColor: '#FFF2E9' },
	'Follow up': { textColor: '#8A3FFC', backgroundColor: '#F3EBFE' },
	Won: { textColor: '#27AE60', backgroundColor: '#E9F7F1' },
	Closed: { textColor: '#6F6F6F', backgroundColor: '#F0F0F0' },
	Sent: { textColor: '#5469D4', backgroundColor: '#D6ECFF' },
	Viewed: { textColor: '#5469D4', backgroundColor: '#E0EBFA' },
	'Change requested': { textColor: '#8A3FFC', backgroundColor: '#F3EBFE' },
	'Pending approval': { textColor: '#FFC107', backgroundColor: '#FFF8E1' },
	Approved: { textColor: '#2BB696', backgroundColor: '#E7F9F0' },
	Canceled: { textColor: '#BA4E00', backgroundColor: '#F8EDE5' },
	Unscheduled: { textColor: '#FFC107', backgroundColor: '#FFF8E1' },
	Scheduled: { textColor: '#DFCE10', backgroundColor: '#FBFAE7' },
	Confirmed: { textColor: '#2BB696', backgroundColor: '#E9F7F4' },
	Completed: { textColor: '#27AE60', backgroundColor: '#E9F7F1' },
	Overdue: { textColor: '#DA1E28', backgroundColor: '#FBE8E9' },
	Paid: { textColor: '#0043CE', backgroundColor: '#E5ECFA' },
	Refunded: { textColor: '#1E3A8A', backgroundColor: '#E0E7FF' },
	'Partially Refunded': {
		textColor: '#1D4ED8',
		backgroundColor: '#DBEAFE',
	},
};

interface StatusBadgeProps {
	status: Status; // Only allows status keys
	className?: string;
	children?: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
	status,
	className,
	children,
}) => {
	const { getLabel } = useOpportunityStatusLabels();
	const statusStyle = statusStyles[status] || {
		textColor: '#000',
		backgroundColor: '#F0F0F0',
	};

	const displayStatus = (() => {
		if (
			status === 'Interested' ||
			status === 'In communication' ||
			status === 'Follow up' ||
			status === 'Won'
		) {
			return getLabel(status as any);
		}
		return status;
	})();

	return (
		<Badge
			className={cn(className, 'w-fit font-semibold text-sm')}
			style={{
				backgroundColor: statusStyle.backgroundColor,
				color: statusStyle.textColor,
			}}
		>
			{children ? children : displayStatus}
		</Badge>
	);
};
